import { FC, MouseEvent, PropsWithChildren, ReactNode, useMemo } from 'react'
import { Link, NavLinkProps as NavLinkNativeProps, useLocation } from 'react-router-dom'

import cx from 'clsx'

import classes from './NavLink.module.scss'

interface NavLinkProps {
  path: string
  className?: string
  icon?: ReactNode
  end?: boolean
  isActive?: NavLinkNativeProps['isActive']
  preserveCase?: boolean
}

const getDefaultIsActive = (pathname: string, to: string, end?: boolean): boolean => {
  if (pathname === to) {
    return true
  }

  if (end) {
    return false
  }

  return pathname.startsWith(`${to}/`)
}

export const NavLink: FC<PropsWithChildren<NavLinkProps>> = ({
  path,
  icon,
  className,
  children,
  end,
  isActive,
  preserveCase,
}) => {
  const location = useLocation()

  const isRouteActive = useMemo(() => {
    const defaultIsActive = getDefaultIsActive(location.pathname, path, end)

    if (isActive) {
      return isActive({ isActive: defaultIsActive, isPending: false }, location)
    }

    return defaultIsActive
  }, [end, isActive, location, path])

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (location.pathname === path) {
      event.preventDefault()
    }
  }

  if (!children && !icon) {
    return null
  }

  return (
    <Link
      aria-current={isRouteActive ? 'page' : undefined}
      className={cx(classes.wrap, className, {
        [classes.active]: isRouteActive,
        [classes.preserveCase]: preserveCase,
      })}
      onClick={handleClick}
      to={path}
    >
      {!!icon && <div className={classes.icon}>{icon}</div>}
      <span>{children}</span>
    </Link>
  )
}
